import { FolderSimple, LinkSimple, TextT, UploadSimple } from '@phosphor-icons/react';
import { Button, SearchField } from '@reelplicate/ui';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { AssetBrowser } from './asset-browser';
import { useMedia } from './media-context';
import { ShowLibraryButton } from './media-layout';
import { useMediaOutlet } from './media-outlet';

/**
 * Figma "Screen — Media · Library" (185:20) and its states: select → add to
 * collection (187:452), AI search results (188:936), asset detail
 * (189:1292) and list + upload (191:2149). `?type=` / `?source=` narrow the
 * library from the Library panel.
 */
export function LibraryPage() {
  const { assets, facets } = useMedia();
  const { newCollection, newCaption, addFromLink, pickFiles } = useMediaOutlet();
  const [params] = useSearchParams();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  const scopes = (['type', 'source'] as const).flatMap((id) => {
    const value = params.get(id);
    const option = facets
      .find((f) => f.id === id)
      ?.options.find((o) => o.value === value);
    return option ? [option] : [];
  });
  const inScope = assets.filter((a) => scopes.every((o) => o.matches(a)));
  // Only the scope remounts the browser: `?asset=` opens a drawer inside it.
  const scopeKey = `${params.get('type')}|${params.get('source')}`;

  return (
    <>
      <header className="flex flex-wrap items-center gap-3">
        <ShowLibraryButton />
        <div className="flex min-w-0 flex-1 items-baseline gap-2.5">
          <h1 className="text-heading-h1 text-text-primary">
            {scopes.length ? scopes.map((o) => o.label).join(' · ') : 'Media'}
          </h1>
          <p className="text-body-md text-text-secondary">
            {inScope.length} {inScope.length === 1 ? 'item' : 'items'}
          </p>
        </div>
        <Button variant="outline" leadingIcon={FolderSimple} onClick={newCollection}>
          New collection
        </Button>
        <Button variant="outline" leadingIcon={TextT} onClick={() => newCaption()}>
          New caption
        </Button>
        <Button variant="outline" leadingIcon={LinkSimple} onClick={() => addFromLink()}>
          Add from link
        </Button>
        <Button leadingIcon={UploadSimple} onClick={() => pickFiles()}>
          Upload
        </Button>
      </header>

      <SearchField
        size="lg"
        aria-label="Search media"
        placeholder="Search titles, tags, captions and transcripts…"
        shortcut="k"
        value={input}
        onValueChange={setInput}
        onSearch={setQuery}
      />

      {/* A fresh browser (filters, selection) for each scope from the panel. */}
      <AssetBrowser key={scopeKey} assets={inScope} query={query} />
    </>
  );
}
